import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { Check, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useUpdateInternalLayerMutation } from 'hooks/internalLayer/useUpdateInternalLayerMutation';
import { fetchInternalLayerVersions } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import { useUscgMapContext } from './UscgMapContext';

export function InternalLayerNameEditor({ layer }: { layer: InternalLayer }) {
  const updateLayer = useUpdateInternalLayerMutation();
  const { setSelectedInternalLayerVersion, setInternalLayerVisible } =
    useUscgMapContext();
  const [name, setName] = useState(layer.name);
  const [editing, setEditing] = useState(false);
  const nameRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setName(layer.name);
  }, [layer.name]);

  useEffect(() => {
    if (editing) nameRef.current?.focus();
  }, [editing]);

  const selectNewestVersion = async (layerId: string) => {
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: layerId },
    });
    const newest = versions.data.reduce<InternalLayerVersion | null>(
      (best, version) => {
        if (!best || version.versionNumber > best.versionNumber) return version;
        return best;
      },
      null
    );
    if (newest) setSelectedInternalLayerVersion(layerId, newest);
    setInternalLayerVisible(layerId, true);
  };

  const save = async () => {
    const next = name.trim();
    if (!next) return;
    await updateLayer.mutateAsync({ internalLayerId: layer.id, name: next });
    await selectNewestVersion(layer.id);
    setEditing(false);
  };

  const cancel = () => {
    setName(layer.name);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="tw:min-w-0 tw:flex-1 tw:truncate tw:rounded-md tw:px-1 tw:py-0.5 tw:text-left tw:text-xs tw:font-semibold tw:hover:bg-accent/60"
        onClick={(event) => {
          event.stopPropagation();
          setEditing(true);
        }}
      >
        {layer.name || 'Untitled layer'}
      </button>
    );
  }

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      <Textarea
        ref={nameRef}
        aria-label="Layer name"
        rows={1}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={(event) => {
          const next = event.relatedTarget;
          if (
            next instanceof Element &&
            next.closest('[data-field-actions="layer-name"]')
          ) {
            return;
          }
          cancel();
        }}
        className="tw:min-h-8 tw:min-w-0 tw:flex-1 tw:resize-none tw:py-1.5 tw:text-xs"
      />
      <div
        data-field-actions="layer-name"
        className="tw:flex tw:shrink-0 tw:items-center tw:gap-1"
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button
          type="button"
          size="icon"
          className="tw:size-8"
          aria-label="Save name"
          disabled={updateLayer.isPending || !name.trim()}
          onClick={() => void save()}
        >
          <Check />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="tw:size-8"
          aria-label="Cancel name"
          disabled={updateLayer.isPending}
          onClick={cancel}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

export function InternalLayerDetailsFields({
  layer,
}: {
  layer: InternalLayer;
}) {
  const updateLayer = useUpdateInternalLayerMutation();
  const { setSelectedInternalLayerVersion, setInternalLayerVisible } =
    useUscgMapContext();
  const [editingField, setEditingField] = useState(false);
  const [savedDescription, setSavedDescription] = useState(
    layer.description ?? ''
  );
  const form = useForm({
    defaultValues: {
      description: layer.description ?? '',
    },
  });

  const selectNewestVersion = async (layerId: string) => {
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: layerId },
    });
    const newest = versions.data.reduce<InternalLayerVersion | null>(
      (best, version) => {
        if (!best || version.versionNumber > best.versionNumber) return version;
        return best;
      },
      null
    );
    if (newest) setSelectedInternalLayerVersion(layerId, newest);
    setInternalLayerVisible(layerId, true);
  };

  const leaveField = () => {
    setEditingField(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  };

  const saveDescription = async () => {
    const description = form.getValues('description').trim() || null;
    await updateLayer.mutateAsync({
      internalLayerId: layer.id,
      description,
    });
    setSavedDescription(description ?? '');
    form.setValue('description', description ?? '');
    await selectNewestVersion(layer.id);
    leaveField();
  };

  const keepEditing = (event: FocusEvent<HTMLTextAreaElement>) => {
    const next = event.relatedTarget;
    return (
      next instanceof Element &&
      Boolean(next.closest('[data-field-actions="description"]'))
    );
  };

  const descriptionField = form.register('description');

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <Field>
        <FieldLabel htmlFor={`layer-description-${layer.id}`}>
          Description
        </FieldLabel>
        <Textarea
          id={`layer-description-${layer.id}`}
          rows={3}
          {...descriptionField}
          onFocus={() => setEditingField(true)}
          onBlur={(event) => {
            void descriptionField.onBlur(event);
            if (keepEditing(event)) return;
            setEditingField(false);
          }}
        />
        {editingField ? (
          <div
            data-field-actions="description"
            className="tw:flex tw:justify-end tw:gap-2"
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            <Button
              type="button"
              size="sm"
              disabled={updateLayer.isPending}
              onClick={() => void saveDescription()}
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label="Cancel description"
              disabled={updateLayer.isPending}
              onClick={() => {
                form.setValue('description', savedDescription);
                leaveField();
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}
      </Field>
    </div>
  );
}
